import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { useDiskStore } from "@/store/diskStore";
import DiskItem from "@/components/disk/DiskItem";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { FolderPlus, Upload, Link as LinkIcon, QrCode, Video } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function DiskPage() {
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const { folders, files, createFolder, uploadFile, init, cleanup, uploadVideo } = useDiskStore();

  const pathFromUrl = searchParams.get("path") || "";
  const isPublicView = !user || String(user?.id) !== "4";

  const [currentPath, setCurrentPath] = useState<string>(pathFromUrl);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [showVideoQRDialog, setShowVideoQRDialog] = useState(false);

  useEffect(() => {
    setCurrentPath(decodeURIComponent(pathFromUrl));
  }, [pathFromUrl]);

  useEffect(() => {
    const effectiveUserId = isPublicView ? "4" : String(user?.id);
    if (effectiveUserId) {
      console.log("[DiskPage] init для", effectiveUserId, isPublicView ? "(public)" : "");
      init(effectiveUserId);
    }
    return () => {
      cleanup();
    };
  }, [user?.id, init, cleanup, isPublicView]);

  const getLevel = (p: string) => (p ? p.split("/").length : 0);

  const currentFolders = folders
    .filter((f) => {
      const folderLevel = getLevel(f.path);
      const currLevel = getLevel(currentPath);

      if (currentPath === "") return folderLevel === 1;
      return f.path.startsWith(currentPath + "/") && folderLevel === currLevel + 1;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const currentFiles = files
    .filter((f) => f.path === currentPath)
    .sort((a, b) => a.name.localeCompare(b.name));

  const currentFolderUrl = `${window.location.origin}/disk?path=${encodeURIComponent(currentPath)}`;

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || isPublicView) return;
    try {
      await createFolder(newFolderName, currentPath, String(user?.id));
      setNewFolderName("");
      setShowNewFolderDialog(false);
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPublicView || !user?.id) return;
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    for (const file of selectedFiles) {
      await uploadFile(file, currentPath, String(user.id));
    }
    e.target.value = "";
  };

  const handleVideoUpload = async () => {
    if (!user || user.id !== "4") {
      toast({ title: "Загрузка видео доступна только для тестовой учётной записи", variant: "destructive" });
      return;
    }

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "video/*";
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const title = prompt("Название видео:");
        const description = prompt("Описание видео:");
        if (title && description) {
          await uploadVideo(file, title, description, currentPath, user.id);
        }
      }
    };
    fileInput.click();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(currentFolderUrl);
    toast({ title: "Ссылка скопирована" });
  };

  const showQR = () => {
    setQrUrl(currentFolderUrl);
    setShowQRDialog(true);
  };

  const showVideoQR = () => {
    const currentFolder = folders.find(f => f.path === currentPath);

    let folderIdForQR = "root"; // для корня
    if (currentFolder) {
      folderIdForQR = currentFolder.id;
    } else if (currentPath !== "") {
      toast({ title: "Не удалось найти папку для видео-фида", variant: "destructive" });
      return;
    }

    // ← Вот главное изменение: добавляем ?path=...
    const videoFeedUrl = `${window.location.origin}/videos/${folderIdForQR}?path=${encodeURIComponent(currentPath)}`;

    setQrUrl(videoFeedUrl);
    setShowVideoQRDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-6 text-white">
      <div className="mx-auto w-full max-w-[95vw] px-4 md:px-8 lg:px-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          {isPublicView ? "Просмотр папки" : "Диск Павлика"}
        </h1>

        {/* Хлебные крошки */}
        <div className="mb-6 flex items-center gap-2 text-purple-300 flex-wrap">
          <span
            className={cn(
              "cursor-pointer hover:text-purple-100 transition-colors",
              isPublicView && "cursor-default"
            )}
            onClick={() => !isPublicView && setCurrentPath("")}
          >
            Корень
          </span>
          {currentPath.split("/").filter(Boolean).map((part, idx, arr) => (
            <span key={idx} className="flex items-center gap-2">
              <span className="text-purple-500">›</span>
              <span
                className={cn(
                  "cursor-pointer hover:text-purple-100 transition-colors",
                  isPublicView && "cursor-default"
                )}
                onClick={() => !isPublicView && setCurrentPath(arr.slice(0, idx + 1).join("/"))}
              >
                {part}
              </span>
            </span>
          ))}
        </div>

        <ContextMenu>
          <ContextMenuTrigger className="min-h-[70vh] rounded-xl border-2 border-dashed border-purple-700/50 bg-purple-950/30 p-6 md:p-8 flex flex-col">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
              {currentFolders.map((folder) => (
                <div
                  key={folder.id}
                  onDoubleClick={() => setCurrentPath(folder.path)}
                  className="cursor-pointer"
                >
                  <DiskItem item={folder} type="folder" isPublic={isPublicView} />
                </div>
              ))}

              {currentFiles.map((file) => (
                <DiskItem key={file.id} item={file} type="file" isPublic={isPublicView} />
              ))}
            </div>

            {currentFolders.length + currentFiles.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-purple-400 text-lg md:text-xl text-center py-20">
                {isPublicView ? "Папка пуста" : "Пусто. Щёлкните правой кнопкой → Создать папку или перетащите файлы"}
              </div>
            )}
          </ContextMenuTrigger>

          {!isPublicView && (
            <ContextMenuContent className="w-64 bg-slate-900 border-purple-700 text-purple-100">
              <ContextMenuItem onSelect={() => setShowNewFolderDialog(true)}>
                <FolderPlus className="h-4 w-4 mr-2" /> Создать папку
              </ContextMenuItem>
              <ContextMenuItem onSelect={() => document.getElementById("file-input")?.click()}>
                <Upload className="h-4 w-4 mr-2" /> Загрузить файлы...
              </ContextMenuItem>
              <ContextMenuItem onSelect={handleVideoUpload}>
                <Video className="h-4 w-4 mr-2" /> Загрузить видео в эту папку
              </ContextMenuItem>
              <ContextMenuSeparator className="bg-purple-700/50" />
              <ContextMenuItem onSelect={copyLink}>
                <LinkIcon className="h-4 w-4 mr-2" /> Поделиться (копировать ссылку)
              </ContextMenuItem>
              <ContextMenuItem onSelect={showQR}>
                <QrCode className="h-4 w-4 mr-2" /> QR-код папки
              </ContextMenuItem>
              <ContextMenuItem onSelect={showVideoQR}>
                <QrCode className="h-4 w-4 mr-2" /> QR-код видео-фида этой папки
              </ContextMenuItem>
            </ContextMenuContent>
          )}
        </ContextMenu>

        {!isPublicView && (
          <>
            <input
              id="file-input"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
              <DialogContent className="bg-slate-900 border-purple-700 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle>Новая папка</DialogTitle>
                  <DialogDescription className="text-purple-300">
                    Введите название новой папки
                  </DialogDescription>
                </DialogHeader>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Название папки"
                  className="bg-slate-800 border-purple-600 text-white"
                  autoFocus
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewFolderDialog(false)}>
                    Отмена
                  </Button>
                  <Button
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Создать
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Диалог QR для обычной папки */}
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent className="bg-slate-900 border-purple-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>QR-код для папки "{currentPath || "Корень"}"</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeSVG value={qrUrl} size={220} level="Q" fgColor="#6b21a8" bgColor="#ffffff" />
              </div>
              <p className="mt-4 text-sm text-purple-300">Отсканируйте камерой телефона</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-4 border-purple-600 text-purple-300 hover:bg-purple-800/50"
                onClick={() => {
                  navigator.clipboard.writeText(qrUrl);
                  toast({ title: "Ссылка скопирована" });
                }}
              >
                Скопировать ссылку
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Диалог QR для видео-фида этой папки */}
        <Dialog open={showVideoQRDialog} onOpenChange={setShowVideoQRDialog}>
          <DialogContent className="bg-slate-900 border-purple-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>QR-код видео-фида папки "{currentPath || "Корень"}"</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeSVG value={qrUrl} size={220} level="Q" fgColor="#6b21a8" bgColor="#ffffff" />
              </div>
              <p className="mt-4 text-sm text-purple-300">Отсканируйте камерой телефона</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-4 border-purple-600 text-purple-300 hover:bg-purple-800/50"
                onClick={() => {
                  navigator.clipboard.writeText(qrUrl);
                  toast({ title: "Ссылка на видео-фид скопирована" });
                }}
              >
                Скопировать ссылку
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}